/**
 * Pure derivation logic for the LifecycleStepper component.
 * Kept free of React/i18n so it is unit-testable (matches the src/lib
 * vitest convention; components in this repo are not unit-tested).
 */

export type Stage =
  | 'uploaded'
  | 'extracting'
  | 'extracted'
  | 'needs_review'
  | 'reviewed'
  | 'booked'
  | 'posted'
  | 'reconciled'
  | 'settled'
  | 'voided';

/** Compact linear order for documents (needs_review rendered inline here). */
export const DOC_LINE: Stage[] = [
  'uploaded',
  'extracting',
  'extracted',
  'needs_review',
  'reviewed',
  'posted',
  'reconciled',
];

/** Main-line order for documents with needs_review reserved as a detour branch. */
export const DOC_MAIN: Stage[] = [
  'uploaded',
  'extracting',
  'extracted',
  'reviewed',
  'posted',
  'reconciled',
];

export const VCH_LINE: Stage[] = ['booked', 'posted', 'reconciled'];

const TERMINALS: Stage[] = ['settled', 'voided'];

/** Every stage a surface may legitimately show (the acceptance set for stageOf). */
const CANONICAL: Stage[] = [...DOC_LINE, ...VCH_LINE, ...TERMINALS];

/**
 * Pre-migration status fallback (deploy-ordering guard; dead after the DB
 * remap). Maps legacy status literals to canonical stages — the UI tolerates
 * rows written before migration rather than stalling or crashing on them.
 * Lane-aware: a journal entry's legacy 'draft' books it (booked), while a
 * document's 'draft' parks it in the review gate (needs_review).
 * Canonical values pass through untouched; unknown values return null so the
 * caller can pick its lane default.
 */
export function mapLegacy(type: 'document' | 'voucher', status: string | null | undefined): Stage | null {
  if (!status) return null;
  if ((CANONICAL as string[]).includes(status)) return status as Stage;
  if (type === 'voucher') {
    switch (status) {
      case 'draft': return 'booked';
      case 'superseded': return 'posted';
      case 'stale': return 'voided';
      default: return null;
    }
  }
  switch (status) {
    case 'draft':
    case 'pending_review': return 'needs_review';
    case 'active':
    case 'sent':
    case 'overdue': return 'reviewed';
    case 'paid': return 'settled';
    case 'cancelled': return 'voided';
    default: return null;
  }
}

/**
 * Stage a mounting surface should render for one row/detail payload.
 * The API's `lifecycle { stage }` block is authoritative — derived doc stages
 * (booked/posted/reconciled) ride on it and are NEVER re-derived in the UI.
 * Only when the block is absent (or its stage is not a canonical stage) does
 * the raw row status go through the legacy map; unresolved garbage falls back
 * to the lane's neutral pending stage so a stepper still renders.
 */
export function stageOf(
  type: 'document' | 'voucher',
  opts: { lifecycle?: { stage?: unknown } | null; status?: string | null; fallback?: Stage },
): Stage {
  const fromBlock = opts.lifecycle?.stage;
  if (typeof fromBlock === 'string' && (CANONICAL as string[]).includes(fromBlock)) {
    return fromBlock as Stage;
  }
  return (
    mapLegacy(type, opts.status ?? null) ??
    opts.fallback ??
    (type === 'voucher' ? 'booked' : 'needs_review')
  );
}

export interface Stepper {
  /** Stages rendered on the main line (compact order or full-variant main line). */
  main: Stage[];
  /** Terminal endcap after the main line, or null when not terminal. */
  terminal: Stage | null;
  /** needs_review detour branch node in the full variant, or null. */
  detour: Stage | null;
  /** Index of the branch node's connection point on DOC_MAIN (after extracted). */
  detourAfter: Stage | null;
  /** Current stage. */
  stage: Stage;
  /** Overall rendering index of the current stage across main (+ detour + terminal). */
  idx: number;
}

/** Rendering state of one node within a plan. */
export interface NodeState {
  past: boolean;
  current: boolean;
  future: boolean;
}

/** Derive a node's rendering state from the plan's current index. */
export function nodeState(plan: Stepper, i: number): NodeState {
  return { past: i < plan.idx, current: i === plan.idx, future: i > plan.idx };
}

/**
 * Node state honoring the detour special case: when the current stage lives
 * on the needs_review branch, no main-line node is "current" — everything up
 * to and including the junction (extracted) is past, the rest is future.
 */
export function planNodeState(plan: Stepper, i: number): NodeState {
  if (plan.detour) {
    return { past: i <= plan.idx, current: false, future: i > plan.idx };
  }
  return nodeState(plan, i);
}

export interface TooltipArgs {
  /** Localized label of the node's stage (resolved by the caller via tr()). */
  label: string;
  /** Localized "you are here" marker. */
  hereLabel: string;
  state: NodeState;
  /** ISO timestamp since the current stage (optional). */
  stageSince?: string;
}

/**
 * Tooltip content for one node. Pure text composition — the lib has no
 * React/i18n imports; callers hand in localized strings from their own
 * tr() tables. Past nodes read "label ✓", future nodes plain (future ones
 * are simply dimmed by CSS), current nodes "label — you are here (date)".
 */
export function tooltipFor({ label, hereLabel, state, stageSince }: TooltipArgs): string {
  if (state.current) {
    const since = stageSince ? ` (${stageSince.slice(0, 10)})` : '';
    return `${label} — ${hereLabel}${since}`;
  }
  return state.past ? `${label} ✓` : label;
}

/**
 * Compute what a stepper renders for a given lifecycle position.
 * - Normal stages: full line, current at its index.
 * - needs_review: detour branch node hanging off the main line
 *   (connected between `extracted` and `reviewed`).
 * - settled/voided: main line cut back to `posted` (inclusive) with the
 *   terminal stage appended as the endcap — the machine never shows
 *   reconciled/settled together.
 */
export function buildStepper(type: 'document' | 'voucher', stage: Stage): Stepper {
  const line = type === 'voucher' ? VCH_LINE : DOC_LINE;

  if (TERMINALS.includes(stage)) {
    const cutEnd = line.indexOf('posted') + 1;
    const main = line.slice(0, Math.max(cutEnd, 1));
    return {
      main,
      terminal: stage,
      detour: null,
      detourAfter: null,
      stage,
      idx: main.length, // terminal sits right after the truncated main line
    };
  }

  const idx = line.indexOf(stage);
  return {
    main: line,
    terminal: null,
    detour: null,
    detourAfter: null,
    stage,
    idx: idx === -1 ? 0 : idx,
  };
}

/** Full-variant layout: needs_review leaves the main line and becomes a branch. */
export function buildFullStepper(type: 'document' | 'voucher', stage: Stage): Stepper {
  const base = buildStepper(type, stage);

  if (stage === 'needs_review') {
    const main = type === 'voucher' ? VCH_LINE : DOC_MAIN;
    const detourAfter = type === 'voucher' ? 'booked' : 'extracted';
    return {
      main,
      terminal: null,
      detour: 'needs_review',
      detourAfter,
      stage,
      idx: main.indexOf(detourAfter),
    };
  }

  return base;
}
